<script setup>
import {useReservationsStore, useRestaurantsStore, useUsersStore, useUserGroupsStore} from '@/stores'
import { useDesignSystem } from '@/composables/useDesignSystem'
import moment from 'moment'
import Tooltip from '@/layouts/components/Tooltip.vue'
import AppDateTimePicker from '@core/components/AppDateTimePicker.vue'
import AdminMembershipEditor from '@core/components/AdminMembershipEditor.vue'
import { membershipEndDate, upcomingMemberships } from '@core/utils/memberships'
import {
  emailValidator,
  requiredValidator,
} from '@validators'

const emit = defineEmits([
  'update:modelValue',
  'submit',
  'update:isDialogVisible'
])

const { dialogWidth } = useDesignSystem()
const dWidth = dialogWidth('md')

const usersStore = useUsersStore()
let adminEditingUserSubscription = null
let myUser = null;
({ myUser, adminEditingUserSubscription } = storeToRefs(usersStore))

const userGroupsStore = useUserGroupsStore()

const isDialogVisible = ref(false)
const isConfirmRemoveDialogOpen = ref(false)

const form = ref()
const valid = ref(true)

const makingNewSubscription = ref(false)


// Shared with the member-facing membership page so the two can't disagree about
// when a not-yet-billed (Pending) membership ends.
const projectedEndDate = subscription => membershipEndDate(subscription)

// Memberships can be stacked one after another — default a new one to start
// the day after the member's latest coverage (current or upcoming) ends.
const nextAvailableStartDate = () => {
  let latestEnd = null
  const byState = adminEditingUserSubscription.value?.platinumSubscriptionsByState
  if (byState?.current?.paidThroughDate) latestEnd = moment(byState.current.paidThroughDate)
  for (const subscription of adminEditingUserSubscription.value?.subscriptions || []) {
    if (subscription.status !== 'Pending') continue
    const end = projectedEndDate(subscription)
    if (end && (!latestEnd || end.isAfter(latestEnd))) latestEnd = end
  }
  if (latestEnd && latestEnd.isSameOrAfter(moment(), 'day')) {
    return latestEnd.add(1, 'day').format('YYYY-MM-DD')
  }
  
  return moment().format('YYYY-MM-DD')
}

const addNewPlatinumSubscription = async () => {
  adminEditingUserSubscription.value.subscriptions.push(  {
    planId: "individual-1cycle-1month",
    status: "New",
    price: 0,
    numberOfBillingCycles: 1,
    numberOfSeats: 1,
    membershipMessage: "",
    firstBillingDate: nextAvailableStartDate(),
    poNumber: "N/A",
    paymentTerms: "None"
  })
  makingNewSubscription.value = true
}

// Only subscriptions on the member's own record can be edited or removed here —
// group-sourced ones are shared by the whole group and are managed from the
// group's dialog.
const isOwnSubscription = subscription =>
  adminEditingUserSubscription.value?.subscriptions?.some(s => s.id === subscription.id)

const membershipError = ref(null)
const savingMembership = ref(false)

// ── Remove ────────────────────────────────────────────────────────────────
const subscriptionToRemove = ref(null)

const requestRemove = subscription => {
  subscriptionToRemove.value = subscription
  isConfirmRemoveDialogOpen.value = true
}

const removeConfirmationMessage = computed(() => {
  const subscription = subscriptionToRemove.value
  if (!subscription) return ''

  const term = `${moment(subscription.firstBillingDate).format('M/D/YY')} - `
    + `${projectedEndDate(subscription)?.format('M/D/YY') || '?'}`

  const paid = Number(subscription.price) > 0
    ? ` This membership was paid ($${Number(subscription.price).toFixed(2)}) — removing it deletes that record here; refund in Stripe separately.`
    : ''

  return `Remove ${subscription.name || subscription.planId} (${term})? `
    + `This deletes it outright, so any access it granted ends immediately. It can't be undone.${paid}`
})

const removeMembership = async () => {
  savingMembership.value = true
  membershipError.value = await usersStore.adminRemoveMembership(
    adminEditingUserSubscription.value.id,
    subscriptionToRemove.value.id,
  )
  savingMembership.value = false
  subscriptionToRemove.value = null
}

// ── Edit ──────────────────────────────────────────────────────────────────
// One membership at a time, edited in place on its own card.
const editingSubscriptionId = ref(null)
const editForm = ref({ planId: null, firstBillingDate: null, membershipMessage: '' })

const startEditing = subscription => {
  membershipError.value = null
  editingSubscriptionId.value = subscription.id
  editForm.value = {
    planId: subscription.planId,
    firstBillingDate: subscription.firstBillingDate,
    membershipMessage: subscription.membershipMessage || '',
  }
}

const cancelEditing = () => {
  editingSubscriptionId.value = null
  membershipError.value = null
}

const saveEditing = async () => {
  savingMembership.value = true
  membershipError.value = await usersStore.adminEditMembership(
    adminEditingUserSubscription.value.id,
    editingSubscriptionId.value,
    editForm.value,
  )
  savingMembership.value = false
  if (!membershipError.value) editingSubscriptionId.value = null
}

// The plan the membership is currently on may no longer be grantable (the
// 1-trial-reservation plan, say). Keep it selectable so editing the start date
// of an old membership doesn't silently switch its plan.
const planOptionsFor = subscription => {
  if (grantablePlans.some(p => p.value === subscription.planId)) return grantablePlans

  return [...grantablePlans, { text: subscription.name || subscription.planId, value: subscription.planId }]
}

// All of the member's own not-yet-started memberships, soonest first. The
// by-state view collapses to a single pending slot, which would hide stacked
// memberships — this lists every one.
const upcomingOwnMemberships = computed(() =>
  upcomingMemberships(adminEditingUserSubscription.value?.subscriptions))

const addNewPlatinumGroup = async () => {
  await userGroupsStore.adminNewPlatinumUserGroupForUser(adminEditingUserSubscription.value)
  await usersStore.cancelAdminEditUserSubscription()
}

const viewPlatinumGroup = async index => {
  //assumes only one group
  await userGroupsStore.adminEditUserGroupSubscription(adminEditingUserSubscription.value.userGroups[index].id)
  await usersStore.cancelAdminEditUserSubscription()
}

const onFormSubmit = async () => {
  form.value.validate().then(({valid: isValid}) => {
    if(isValid){
      usersStore.saveAdminEditingUserSubscription()

    }

  })
}

//check to see if we should pop up any dialogs when the store changes
//this seems hacky
usersStore.$subscribe((mutation, state) => {
  //if wasn't visible before and now is, reset some things
  if(!isDialogVisible.value && (state.adminEditingUserSubscription != null)) makingNewSubscription.value = false

  isDialogVisible.value = ( state.adminEditingUserSubscription != null)
})

//this is part of a hacky fix for https://github.com/vuetifyjs/vuetify/issues/17017
// by setting scroll-strategy="none" below it fixes an issue with the position of the dialog
//then this watchEffect prevents the page from scrolling when the dialog is open
watchEffect(() => {
  document.documentElement.style.overflow = isDialogVisible.value ? 'hidden' : null
})

// Plans an admin can grant. The 1-trial-reservation plan (a '-1free' planId) is
// deliberately absent: models/User.js labels any '-1free' plan 'Trial', and
// EditReservationDialog then limits the member to a single reservation for the
// whole term — so granting it as a comp membership silently restricts the member
// rather than rewarding them. Existing '-1free' memberships still render fine.
const grantablePlans = [
  {text: '1 Month', value: 'individual-1cycle-1month'},
  {text: '2 Months', value: 'individual-1cycle-2month'},
  {text: '3 Months', value: 'individual-1cycle-3month'},
  {text: 'Annual', value: 'individual-1cycle-annual'}
]
</script>

<template>
  <ConfirmDialog
    v-model:is-dialog-visible="isConfirmRemoveDialogOpen"
    :confirmation-msg="removeConfirmationMessage"
    confirm-btn-text="Remove"
    cancel-btn-text="Keep"
    @confirm="removeMembership"
  />
  <VDialog
    v-if="adminEditingUserSubscription && myUser.userLevel == 'admin'"
    v-model="isDialogVisible"
    :width="dWidth"
    persistent
    fullscreen
  >
    <VToolbar
      dark
      color="primary"
      class="toolbar-header"
    >
      <VToolbarItems style="width: 58px" />
      <VSpacer />
      <VToolbarTitle>Membership</VToolbarTitle>
      <VSpacer />
      <VToolbarItems>
        <VBtn
          icon="$close"
          size="large"
          variant="text"
          @click="usersStore.cancelAdminEditUserSubscription()"
        >
          <VIcon>mdi-close</VIcon>
        </VBtn>
      </VToolbarItems>
    </VToolbar>
    <VCard class="pt-0 ps-0 main-content">
      <VCardText>
        <!-- 👉 Form -->
        <VForm
          ref="form"
          @submit.prevent="onFormSubmit"
        >
          <VRow>
            <VCol
              cols="12"
            >
              <div class="">
                <VCard
                  v-if="makingNewSubscription"
                  title="Add Free Platinum Membership"
                  class="ma-2 pa-2"
                >
                  <VSelect
                    v-model="adminEditingUserSubscription.subscriptions[adminEditingUserSubscription.subscriptions.length-1].planId"
                    class="pa-4"
                    label="Plan"
                    :items="grantablePlans"
                    item-title="text"
                    item-value="value"
                  />
                  <div class="pa-4">
                    <!--
                      No min date: a comp membership is often granted after the
                      fact, so it has to be datable to when the member actually
                      earned it. A backdated grant starts Active (or Complete
                      if its term has already run out). 
                    -->
                    <AppDateTimePicker
                      v-model="adminEditingUserSubscription.subscriptions[adminEditingUserSubscription.subscriptions.length-1].firstBillingDate"
                      class="pa-4"
                      label="Start Date"
                      :config="{ disableMobile: true, altInput: true, altFormat: 'l, n/j', dateFormat: 'Y-m-d', defaultDate: moment().format('YYYY-MM-DD')}"
                      :rules="[requiredValidator]"
                      required
                    />
                  </div>

                  <VTextField
                    v-model="adminEditingUserSubscription.subscriptions[adminEditingUserSubscription.subscriptions.length-1].membershipMessage"
                    class="pa-4"
                    label="Membership Message"
                  />
                  <VCardActions>
                    <VBtn
                      @click="usersStore.saveAdminEditingUserSubscription(); makingNewSubscription=false;"
                    >
                      Save
                    </VBtn>
                  </VCardActions>
                </VCard>
                <div v-else>
                  <VBtn
                    class="ml-2 mt-2"
                    @click="addNewPlatinumSubscription"
                  >
                    New Platinum Membership
                  </VBtn>
                  <div class="ml-2 mt-1 text-caption text-medium-emphasis">
                    Memberships can be stacked back to back — a new one defaults to starting the day after the member's latest coverage ends.
                  </div>
                </div>
              </div>
              <VDivider class="ma-4" />

              <h3 class="">
                Platinum Memberships
              </h3>
              <template
                v-for="(subscription, key) in adminEditingUserSubscription.platinumSubscriptionsByState"
                :key="key"
              >
                <VCard
                  v-if="subscription && !(key === 'pending' && isOwnSubscription(subscription))"
                  class="ma-2 pa-2"
                >
                  <h4 class="">
                    {{ subscription.status }} Platinum
                  </h4>
                  <div><b><u>{{ subscription.name }}</u></b></div>
                  <!--                  <div>Billing Period: {{subscription.billingPeriod}}</div> -->
                  <div v-if="subscription?.discountGroupMembershipMessage">
                    <b>Membership Message: </b>{{ subscription.discountGroupMembershipMessage }}
                  </div>
                  <div v-if="subscription?.sourceGroupMembershipMessage">
                    <b>Membership Message: </b>{{ subscription.sourceGroupMembershipMessage }}
                  </div>
                  <div v-if="subscription?.sourceUserName">
                    <b>Primary Member: </b>{{ subscription.sourceUserName }} ({{ subscription.sourceUserEmail }})
                  </div>
                  <div v-if="subscription?.membershipMessage">
                    <b>Membership Message: </b>{{ subscription.membershipMessage }}
                  </div>
                  <div><b>Cost:</b> ${{ (subscription?.price).toFixed(2) }}</div>
                  <div v-if="subscription?.balance">
                    Balance owed: ${{ (subscription.balance).toFixed(2) }}
                  </div>
                  <!--                  <div>First Billing Date: {{ moment(subscription.firstBillingDate).format("YYYY-MM-DD")}}</div> -->
                  <div v-if="subscription?.numberOfBillingCycles && subscription?.numberOfBillingCycles == 1">
                    <b>Term:</b> {{ moment(subscription.firstBillingDate).format("M/D/YY") }} - {{ moment(subscription.paidThroughDate).format("M/D/YY") }}
                  </div>
                  <div v-if="subscription.nextBillingDate">
                    Next Billing Date: {{ subscription.nextBillingDate ? moment(subscription.nextBillingDate).format("YYYY-MM-DD") : "N/A" }}
                  </div>
                  <div
                    v-if="key === 'pending'"
                    class="mt-2 text-caption text-medium-emphasis"
                  >
                    This pending membership comes from a group — manage it from the group's dialog below.
                  </div>

                  <AdminMembershipEditor
                    v-if="editingSubscriptionId === subscription.id"
                    v-model="editForm"
                    :plan-options="planOptionsFor(subscription)"
                    :saving="savingMembership"
                    :error="membershipError"
                    @save="saveEditing"
                    @cancel="cancelEditing"
                  />
                  <div
                    v-else-if="isOwnSubscription(subscription)"
                    class="d-flex ga-2 mt-3"
                  >
                    <VBtn
                      variant="tonal"
                      size="small"
                      @click="startEditing(subscription)"
                    >
                      Edit
                    </VBtn>
                    <VBtn
                      color="error"
                      variant="text"
                      size="small"
                      @click="requestRemove(subscription)"
                    >
                      Remove
                    </VBtn>
                  </div>
                  <!--              <div>{{ subscription}}</div> -->
                </VCard>
              </template>
              <div v-if="!adminEditingUserSubscription.isPlatinum">
                No Current Platinum Memberships
              </div>

              <template v-if="upcomingOwnMemberships.length">
                <h3 class="mt-4">
                  Upcoming Memberships
                </h3>
                <VCard
                  v-for="subscription in upcomingOwnMemberships"
                  :key="subscription.id || subscription.firstBillingDate"
                  class="ma-2 pa-2"
                >
                  <h4>Pending {{ subscription.name || subscription.planId }}</h4>
                  <div v-if="subscription.membershipMessage">
                    <b>Membership Message: </b>{{ subscription.membershipMessage }}
                  </div>
                  <div><b>Cost:</b> ${{ Number(subscription.price || 0).toFixed(2) }}</div>
                  <div>
                    <b>Term:</b> {{ moment(subscription.firstBillingDate).format("M/D/YY") }} -
                    {{ projectedEndDate(subscription) ? projectedEndDate(subscription).format("M/D/YY") : "?" }}
                  </div>
                  <AdminMembershipEditor
                    v-if="editingSubscriptionId === subscription.id"
                    v-model="editForm"
                    :plan-options="planOptionsFor(subscription)"
                    :saving="savingMembership"
                    :error="membershipError"
                    @save="saveEditing"
                    @cancel="cancelEditing"
                  />
                  <div
                    v-else
                    class="d-flex ga-2 mt-3"
                  >
                    <VBtn
                      variant="tonal"
                      size="small"
                      @click="startEditing(subscription)"
                    >
                      Edit
                    </VBtn>
                    <VBtn
                      color="error"
                      variant="text"
                      size="small"
                      @click="requestRemove(subscription)"
                    >
                      Remove
                    </VBtn>
                  </div>
                </VCard>
              </template>

              <VDivider class="ma-4" />
              <div>
                <h3 class="">
                  Group Connections
                </h3>
                <div class="ml-4">
                  <div v-if="adminEditingUserSubscription.primaryGroupName">
                    <div><b>Primary Group:</b> {{ adminEditingUserSubscription.primaryGroupName }} ({{ adminEditingUserSubscription.primaryGroupType }})<br><b>Primary Group Permission:</b> {{ adminEditingUserSubscription.primaryGroupPermission }}</div>
                  </div>
                  <VAlert
                    v-if="adminEditingUserSubscription.userGroups.length > 1"
                    type="warning"
                  >
                    Member of multiple groups! Please remove from one
                  </VAlert>
                  <VAlert
                    v-if="adminEditingUserSubscription.primaryGroupType && !adminEditingUserSubscription.isPlatinum"
                    type="info"
                  >
                    Remove member from existing group to connect to another
                  </VAlert>
                  <VBtn
                    v-if="adminEditingUserSubscription.userGroups.length == 0"
                    class="mt-4 ml-4"
                    @click="addNewPlatinumGroup"
                  >
                    New Platinum Group
                  </VBtn>
                  <div />

                  <template
                    v-for="(group, index) in adminEditingUserSubscription.userGroups"
                    :key="index"
                  >
                    <VBtn
                      class="mt-4 ml-4"
                      variant="tonal"
                      @click="viewPlatinumGroup(index)"
                    >
                      View {{ group.name }}
                    </VBtn>
                  </template>
                </div>
              </div>
            </VCol>
            <VCol
              cols="12"
            />
          </VRow>
        </VForm>
      </VCardText>
    </VCard>
  </VDialog>
</template>


<style lang="scss" scoped>
.v-selection-control-group {
  display: flex;
  flex-direction: unset;
}

.v-toolbar-title{
  flex: unset;
}

.toolbar-header{
  position: fixed;
  z-index: 1000;
}

.main-content{
  top: 60px;
  padding-bottom: 60px;
  position: relative;
}
</style>
